import React, { useMemo } from 'react';
import Slider from 'react-slick';
import styled from 'astroturf/react';
import TextTruncate from 'react-text-truncate';
import routes from 'js/core_pre_webpack/routes';
import $image_url from 'js/utils/image_url';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import './carousel.scss';

export interface CarouselProps {
  categoryTitle: string;
  courses: Array<CourseCardProps>;
  page: string; // For tracking purposes, TODO: find a better way
  courseImpressionData?: { [key: string]: unknown };
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  a {
    text-decoration: none !important;
  }

  &:not(:last-child) {
    margin-bottom: 48px;
  }
`;

const Header = styled.div`
  margin-bottom: 18px;

  font-family: Roboto;
  font-size: 28px;
  line-height: 34px;

  color: #222222;

  opacity: 0.9;

  @media screen and (min-width: 320px) {
    padding-left: 16px;
  }

  @media screen and (min-width: 1024px) {
    padding-left: 50px;
  }

  @media screen and (min-width: 1366px) {
    --leftover: calc(100% - 1130px);
    padding-left: calc(var(--leftover) / 2);
  }
`;

const CourseCardHoverIcon = styled.img`
  z-index: 1;
  display: none;
  position: absolute;
  height: 47px;
  width: 47px;
  right: calc(50% - 23.5px);
  top: calc(50% - 23.5px);
`;

const CourseCardContainer = styled.div`
  background: #fff;
  height: 260px;
  box-shadow: 0px 3px 16px 0px rgba(0, 0, 0, 0.05);
  border-radius: 3px;
  overflow: hidden;

  // Force hardware acceleration because course thumbnail doesn't seem
  // to render properly on mobile safari until user interacts with the
  // element.
  // https://stackoverflow.com/questions/8293978/single-finger-scroll-in-safari-not-rendering-html-until-scroll-finishes
  -webkit-transform: translate3d(0, 0, 0);

  @media screen and (min-width: 1366px) {
    height: 290px;
  }

  &:hover {
    ${CourseCardHoverIcon} {
      display: block;
    }
  }
`;

const CourseCardImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 123px;
  overflow: hidden;

  @media screen and (min-width: 1366px) {
    height: 152px;
  }
`;

const CourseCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
`;

const CourseCardInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

const CourseCardTitle = styled.div`
  font-family: Roboto;
  font-weight: bold;
  font-size: 15px;
  line-height: 20px;

  color: #222222;
  margin-bottom: 8px;
`;

const NewBadge = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  padding: 0.3em 0.5em;
  border-bottom-right-radius: 3px;
  background: #0954d3;
  color: #ffffff;
  user-select: none;

  &::after {
    content: 'NEW';
    font-size: 0.9em;
  }
`;

const CourseCardDetails = styled.div`
  font-family: Roboto;
  font-size: 13px;
  line-height: 16px;
  color: #6b7482;
`;

const CourseCardInstructorName = styled.div`
  height: 24px;
  font-family: Roboto;
  font-size: 13px;
  color: #222222;
`;

export interface CourseCardProps {
  courseId: string;
  thumbnailPath: string;
  title: string;
  numVideos: number;
  duration: string;
  instructorName: string;
  isNew: boolean;
  impressionData?: { [key: string]: unknown };
  containerStyle?: React.CSSProperties;
  imageContainerStyle?: React.CSSProperties;
}

interface CourseCardParams {
  id: string;
}

export function CourseCard(props: CourseCardProps): React.ReactElement {
  const {
    courseId,
    thumbnailPath,
    title,
    numVideos,
    duration,
    instructorName,
    isNew,
    containerStyle,
    imageContainerStyle,
  } = props;

  const courseCardParams: CourseCardParams = { id: courseId };

  return (
    <CourseCardContainer style={containerStyle}>
      <a
        href={routes.course_url(courseCardParams)}
        style={{ textDecoration: 'none' }}
      >
        <CourseCardImageContainer style={imageContainerStyle}>
          {isNew && <NewBadge />}
          <CourseCardImage src={$image_url(thumbnailPath)} />
          <CourseCardHoverIcon
            src={$image_url('courses/course-card-play-icon.svg')}
          />
        </CourseCardImageContainer>
        <CourseCardInfoContainer>
          <div style={{ height: '78px', marginBottom: '16px' }}>
            <CourseCardTitle>
              <TextTruncate line={3} text={title} />
            </CourseCardTitle>
            <CourseCardDetails>
              {numVideos} videos, {duration}
            </CourseCardDetails>
          </div>
          <CourseCardInstructorName>{instructorName}</CourseCardInstructorName>
        </CourseCardInfoContainer>
      </a>
    </CourseCardContainer>
  );
}

export default function Carousel(props: CarouselProps): React.ReactElement {
  const { categoryTitle, courses, page, courseImpressionData } = props;

  const items = courses.map((course, index) => {
    const category = categoryTitle.toLowerCase().replace(/[^\w]+/g, '_');
    return (
      <ImpressionWatcher
        source={category}
        entity_type={9}
        entity_id={course.courseId}
        extra={{
          position: index,
          page: page,
          ...courseImpressionData,
          ...course.impressionData,
        }}
        // The CourseCard is rather large and contains the thumbnail, title, instructor, etc.
        // Assuming that the user is scrolling from the top of the page, a threshold of 40% will
        // mean that most of the thumbnail should be displayed in the viewport. Anything less than
        // that will not be registered as an impression and any clicks to the CourseCard in that
        // scenario shall be regarded as an accidental click.
        threshold={0.4}
      >
        <CourseCard
          courseId={course.courseId}
          thumbnailPath={course.thumbnailPath}
          title={course.title}
          numVideos={course.numVideos}
          duration={course.duration}
          instructorName={course.instructorName}
          isNew={course.isNew}
        />
      </ImpressionWatcher>
    );
  });

  const carousel = useMemo(() => {
    if (items.length === 0) return null;

    return (
      <Slider
        swipeToSlide
        slidesToShow={4}
        infinite={items.length > 4}
        responsive={[
          {
            breakpoint: 1000,
            settings: { slidesToShow: 3, infinite: items.length > 3 },
          },
          {
            breakpoint: 760,
            settings: { slidesToShow: 2, infinite: items.length > 2 },
          },
          {
            breakpoint: 450,
            settings: { slidesToShow: 1, infinite: items.length > 1 },
          },
        ]}
      >
        {items}
      </Slider>
    );
  }, [items]);

  return (
    <Container>
      <Header>{categoryTitle}</Header>
      {carousel}
    </Container>
  );
}
